/**
 * Enregistre tous les attributs du panier (#cart) en une seule requête,
 * avec debounce sur la saisie et flush avant le passage en caisse.
 * Requiert global.js (fetchConfig) et window.routes (theme.liquid).
 */
(function () {
  const DEBOUNCE_MS = 600;

  /** Timer du debounce pour ne pas envoyer une requête à chaque frappe. */
  let debounceTimer = null;
  /** Évite une boucle infinie : le 2e submit laisse le navigateur poster le formulaire (checkout). */
  let allowNativeSubmit = false;

  /** Transforme `attributes[Nom complet]` (name HTML) en clé API `Nom complet`. */
  function parseAttributeKey(inputName) {
    const m = /^attributes\[(.+)\]$/.exec(inputName);
    return m ? m[1] : inputName;
  }

  /** Lit tous les champs `attributes[...]` du formulaire et construit l’objet attendu par l’API. */
  function collectAttributes(form) {
    const attributes = {};
    form.querySelectorAll('input[name^="attributes"], textarea[name^="attributes"]').forEach((el) => {
      const name = el.getAttribute('name');
      if (!name) return;
      attributes[parseAttributeKey(name)] = el.value;
    });
    return attributes;
  }

  /** POST `/cart/update.js` avec tous les attributs en une fois. */
  function saveCartAttributes(form) {
    if (!form || !window.routes || !window.routes.cart_update_url) {
      return Promise.resolve();
    }

    const body = JSON.stringify({
      attributes: collectAttributes(form),
    });

    return fetch(window.routes.cart_update_url, {
      ...fetchConfig(),
      body,
    }).then((response) => {
      if (!response.ok) throw new Error('Cart attributes update failed');
      return response.json();
    });
  }

  /** Lance (ou reporte) une sauvegarde après `DEBOUNCE_MS` sans frappe. */
  function scheduleSave(form) {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      saveCartAttributes(form).catch((e) => console.error(e));
    }, DEBOUNCE_MS);
  }

  /** Attache input/change (debounce) et submit (sauvegarde synchrone puis envoi réel du formulaire). */
  function bindCartForm(form) {
    if (form.dataset.cartAttributesBound) return;
    form.dataset.cartAttributesBound = 'true';

    form.addEventListener(
      'input',
      (e) => {
        if (!e.target.matches('input[name^="attributes"], textarea[name^="attributes"]')) return;
        scheduleSave(form);
      },
      true
    );

    form.addEventListener(
      'change',
      (e) => {
        if (!e.target.matches('input[name^="attributes"], textarea[name^="attributes"]')) return;
        scheduleSave(form);
      },
      true
    );

    form.addEventListener(
      'submit',
      async (e) => {
        if (allowNativeSubmit) {
          allowNativeSubmit = false;
          return;
        }

        if (!form.checkValidity()) return;

        e.preventDefault();

        const submitter = e.submitter;

        clearTimeout(debounceTimer);
        debounceTimer = null;

        try {
          await saveCartAttributes(form);
        } catch (err) {
          console.error(err);
          const msg = window.cartStrings && window.cartStrings.error ? window.cartStrings.error : 'Unable to save. Please try again.';
          window.alert(msg);
          return;
        }

        allowNativeSubmit = true;
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit(submitter || undefined);
        } else {
          const hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.name = 'checkout';
          hidden.value = '1';
          form.appendChild(hidden);
          HTMLFormElement.prototype.submit.call(form);
          form.removeChild(hidden);
        }
      },
      true
    );
  }

  /** Point d’entrée : formulaire `#cart` avec au moins un champ attribut. */
  function init() {
    const form = document.getElementById('cart');
    if (!form || !form.querySelector('[name^="attributes"]')) return;
    bindCartForm(form);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init(); // DOM déjà prêt (script en bas de page ou rechargement dynamique)
  }
})();
